export type Environment = 'UAT' | 'Production' | 'EUWE2_UAT' | 'EUWE2_Production';

export interface EnvironmentConfig {
  value: Environment;
  label: string;
  descriptor: 'uat' | 'prod' | 'euwe2-uat' | 'euwe2-prod';
  apiBaseUrl: string;
  /**
   * The operations-portal origin/referer the API expects for this environment.
   * EUWE2 enforces strict origin checks and 500s on a mismatch, so this must
   * match the region's actual portal (e.g. operations-portal-uat-euwe2.liftango.com).
   */
  portalOrigin: string;
  tokenStorageKey: string;
  productStorageKey: string;
  isProduction: boolean;
}

export const ENVIRONMENT_CONFIGS: Record<Environment, EnvironmentConfig> = {
  UAT: {
    value: 'UAT',
    label: 'UAT',
    descriptor: 'uat',
    apiBaseUrl: 'https://hailer-odb-uat.liftango.com',
    portalOrigin: 'https://operations-portal-uat.liftango.com',
    tokenStorageKey: 'atlas_uatJwtToken',
    productStorageKey: 'atlas_lastUATProductId',
    isProduction: false,
  },
  Production: {
    value: 'Production',
    label: 'Prod',
    descriptor: 'prod',
    apiBaseUrl: 'https://hailer-odb-prod.liftango.com',
    portalOrigin: 'https://operations-portal.liftango.com',
    tokenStorageKey: 'atlas_prodJwtToken',
    productStorageKey: 'atlas_lastProdProductId',
    isProduction: true,
  },
  EUWE2_UAT: {
    value: 'EUWE2_UAT',
    label: 'EUWE2 UAT',
    descriptor: 'euwe2-uat',
    apiBaseUrl: 'https://liftango-api-uat-euwe2.liftango.com',
    portalOrigin: 'https://operations-portal-uat-euwe2.liftango.com',
    tokenStorageKey: 'atlas_euwe2UatJwtToken',
    productStorageKey: 'atlas_lastEUWE2UATProductId',
    isProduction: false,
  },
  EUWE2_Production: {
    value: 'EUWE2_Production',
    label: 'EUWE2 Prod',
    descriptor: 'euwe2-prod',
    apiBaseUrl: 'https://liftango-api-prod-euwe2.liftango.com',
    portalOrigin: 'https://operations-portal-euwe2.liftango.com',
    tokenStorageKey: 'atlas_euwe2ProdJwtToken',
    productStorageKey: 'atlas_lastEUWE2ProdProductId',
    isProduction: true,
  },
};

export const ENVIRONMENT_OPTIONS = Object.values(ENVIRONMENT_CONFIGS);

export function getEnvironmentConfig(environment: Environment): EnvironmentConfig {
  return ENVIRONMENT_CONFIGS[environment] ?? ENVIRONMENT_CONFIGS.UAT;
}

export function getEnvironmentDisplayName(environment: Environment): string {
  return getEnvironmentConfig(environment).label;
}

export function getEnvironmentDescriptor(environment: Environment): EnvironmentConfig['descriptor'] {
  return getEnvironmentConfig(environment).descriptor;
}

export function getEnvironmentApiBaseUrl(environment: Environment): string {
  return getEnvironmentConfig(environment).apiBaseUrl;
}

export function getEnvironmentPortalOrigin(environment: Environment): string {
  return getEnvironmentConfig(environment).portalOrigin;
}

export function getEnvironmentTokenStorageKey(environment: Environment): string {
  return getEnvironmentConfig(environment).tokenStorageKey;
}

export function getEnvironmentProductStorageKey(environment: Environment): string {
  return getEnvironmentConfig(environment).productStorageKey;
}

export function isProductionEnvironment(environment: Environment): boolean {
  return getEnvironmentConfig(environment).isProduction;
}
